import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { draftJournalsApi, draftPaymentsApi } from '@/services/paymentsApiService';

interface DraftJournalsState {
  draftJournals: any[];
  draftPayments: any[];
  loading: boolean;
  error: string | null;
  /** In-flight count: journals and payments are fetched independently. */
  pendingCount: number;
}

const initialState: DraftJournalsState = {
  draftJournals: [],
  draftPayments: [],
  loading: false,
  error: null,
  pendingCount: 0,
};

/**
 * Normalize the list responses: `response.data.data || response.data` accepted
 * any shape, so a differently-wrapped or empty body left a non-array in state
 * and broke every downstream `.map`/`.length`.
 */
const toList = (response: any): any[] => {
  const payload = response?.data?.data ?? response?.data;
  return Array.isArray(payload) ? payload : [];
};

/**
 * `err.response.data` is a raw server envelope (possibly undefined), so
 * rejecting with it stored a non-string / undefined in state. Reduce to a
 * serializable message.
 */
const toSafeError = (err: any): string => {
  const payload = err?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof err?.message === 'string' ? err.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
};

export const fetchDraftJournals = createAsyncThunk(
  'draftJournals/fetchDraftJournals',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await draftJournalsApi.getAll(params || {});
      return toList(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const fetchDraftPayments = createAsyncThunk(
  'draftJournals/fetchDraftPayments',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await draftPaymentsApi.getAll(params || {});
      return toList(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

const draftJournalsSlice = createSlice({
  name: 'draftJournals',
  initialState,
  reducers: {
    resetDraftJournals(state) {
      // Fresh arrays: Object.assign copied the SHARED initialState array
      // references into the store, so a later mutation also mutated the
      // baseline and corrupted every subsequent reset.
      state.draftJournals = [];
      state.draftPayments = [];
      state.loading = false;
      state.error = null;
      state.pendingCount = 0;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: DraftJournalsState) => {
      state.pendingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const handleFulfilled = (state: DraftJournalsState) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
    };
    const handleRejected = (state: DraftJournalsState, action: any) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
      state.error = typeof action.payload === 'string' ? action.payload : null;
    };

    builder
      .addCase(fetchDraftJournals.pending, handlePending)
      .addCase(fetchDraftJournals.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.draftJournals = action.payload;
      })
      .addCase(fetchDraftJournals.rejected, handleRejected)
      .addCase(fetchDraftPayments.pending, handlePending)
      .addCase(fetchDraftPayments.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.draftPayments = action.payload;
      })
      .addCase(fetchDraftPayments.rejected, handleRejected);
  },
});

export const { resetDraftJournals } = draftJournalsSlice.actions;

export const selectDraftJournals = (state: any) => state.draftJournals.draftJournals;
export const selectDraftPayments = (state: any) => state.draftJournals.draftPayments;
export const selectDraftJournalsLoading = (state: any) => state.draftJournals.loading;
export const selectDraftJournalsError = (state: any) => state.draftJournals.error;

export default draftJournalsSlice.reducer;
